import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PawMark } from "@/components/PawMark";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          clearInterval(timer);
        } else if (tries > 15) {
          setStatus("pending");
          clearInterval(timer);
        }
      } catch {
        if (tries > 15) {
          setStatus("pending");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId, clear]);

  return (
    <div className="min-h-screen bg-[#1C1F1E] flex items-center justify-center px-5">
      <div className="grain-overlay" aria-hidden="true" />
      <div data-testid="payment-success-page" className="max-w-lg w-full text-center">
        <PawMark className="w-12 h-12 text-[#B5A642] mx-auto" />
        {status === "checking" && (
          <>
            <h1 className="mt-8 font-display font-light text-4xl text-[#E8DDC7]">Consulting the till…</h1>
            <p className="mt-4 text-[#D3CEC4]/60">One moment while we confirm with the counting house.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <h1 className="mt-8 font-display font-light text-5xl text-[#E8DDC7]" data-testid="payment-confirmed-heading">
              The order is sealed.
            </h1>
            <p className="mt-5 text-base text-[#D3CEC4]/70 leading-relaxed">
              Your preparations are being wrapped in paper and string. When the jar arrives,
              bring its batch number to the Ledger and read where it grew.
            </p>
            <p className="mt-3 font-display italic text-[#B5A642]/80">The dogs have been notified. They are thrilled, probably.</p>
          </>
        )}
        {(status === "pending" || status === "missing") && (
          <>
            <h1 className="mt-8 font-display font-light text-4xl text-[#E8DDC7]">The ink is still drying.</h1>
            <p className="mt-4 text-[#D3CEC4]/60">
              We could not confirm the payment just yet. If the charge completed, it will appear shortly.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="return-to-apothecary"
          className="mt-10 inline-block px-7 py-3.5 border border-[#B5A642]/70 text-[#D3CEC4] text-[12px] uppercase tracking-[0.2em] rounded-sm hover:bg-[#B5A642]/10 transition-colors duration-300"
        >
          Return to the Apothecary
        </Link>
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen bg-[#1C1F1E] flex items-center justify-center px-5">
    <div className="grain-overlay" aria-hidden="true" />
    <div data-testid="payment-cancel-page" className="max-w-lg w-full text-center">
      <PawMark className="w-12 h-12 text-[#B5A642] mx-auto" />
      <h1 className="mt-8 font-display font-light text-5xl text-[#E8DDC7]">The drawer stays open.</h1>
      <p className="mt-5 text-base text-[#D3CEC4]/70 leading-relaxed">
        Nothing was charged, and nothing was judged. The cabinet will keep your selections a little longer.
      </p>
      <Link
        to="/"
        data-testid="cancel-return-link"
        className="mt-10 inline-block px-7 py-3.5 bg-[#4B3226] text-[#E8DDC7] text-[12px] uppercase tracking-[0.2em] rounded-sm hover:brightness-125 transition-[filter] duration-300"
      >
        Back to the shop
      </Link>
    </div>
  </div>
);
